import { useEffect } from 'react';
import { Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { DiscountTypeEnum } from '@libs/schemas';
import { NumericFormat } from 'react-number-format';
import { safeMoneyFromCents } from '@libs/services/money';
import { DiscountData } from '@libs/schemas/src/pricing-rule';
import { ConfigurationFieldValue } from 'components/pricing/PricingRuleSheet';

interface ErrorRecord {
  [key: string]: string;
}

interface DiscountConfigurationFormProps {
  discountData: DiscountData;
  errors: ErrorRecord;
  isAutomatic: boolean;
  onConfigurationChange: (field: string, value: ConfigurationFieldValue) => void;
}

export function DiscountConfigurationForm({
  discountData,
  errors,
  isAutomatic,
  onConfigurationChange,
}: DiscountConfigurationFormProps) {
  const currentDiscountType = discountData.discountType || DiscountTypeEnum.PERCENTAGE;
  const isPercentageType = currentDiscountType === DiscountTypeEnum.PERCENTAGE;

  useEffect(() => {
    if (!discountData.discountType) {
      onConfigurationChange('data.discountType', DiscountTypeEnum.PERCENTAGE);
    }
  }, [discountData.discountType, onConfigurationChange]);

  // Clear the opposite discount field when discount type changes
  useEffect(() => {
    if (currentDiscountType === DiscountTypeEnum.PERCENTAGE) {
      // Clear flat amount when switching to percentage
      if (
        discountData.discountAmountInCents !== undefined &&
        discountData.discountAmountInCents !== null
      ) {
        onConfigurationChange('data.discountAmountInCents', undefined);
      }
    } else if (currentDiscountType === DiscountTypeEnum.FLAT) {
      // Clear percentage when switching to flat amount
      if (discountData.discountPercent !== undefined && discountData.discountPercent !== null) {
        onConfigurationChange('data.discountPercent', undefined);
      }
    }
  }, [
    currentDiscountType,
    discountData.discountAmountInCents,
    discountData.discountPercent,
    onConfigurationChange,
  ]);

  return (
    <div className="space-y-6">
      {isAutomatic && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Auto-Applied:</strong> This discount will be automatically applied to qualifying
            orders without requiring any customer input.
          </AlertDescription>
        </Alert>
      )}

      {!isAutomatic && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Manual Application:</strong> This discount will not be applied automatically and
            requires separate implementation to activate.
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label htmlFor="discountType" className="text-sm font-medium">
          Discount Type
        </Label>
        <Select
          value={currentDiscountType}
          onValueChange={(value: DiscountTypeEnum.PERCENTAGE | DiscountTypeEnum.FLAT) => {
            onConfigurationChange('data.discountType', value);
          }}
        >
          <SelectTrigger className="mt-1.5">
            <SelectValue placeholder="Choose a discount type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DiscountTypeEnum.FLAT}>
              <div className="flex items-center space-x-2">
                <span>💵</span>
                <span>Fixed Amount Off</span>
              </div>
            </SelectItem>
            <SelectItem value={DiscountTypeEnum.PERCENTAGE}>
              <div className="flex items-center space-x-2">
                <span>📊</span>
                <span>Percentage Off</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {isPercentageType ? (
        <div>
          <Label htmlFor="discountPercent" className="text-sm font-medium">
            Discount Percentage
          </Label>
          <div className="relative mt-1.5">
            <NumericFormat
              customInput={Input}
              decimalScale={2}
              fixedDecimalScale={true}
              id="discountPercent"
              type="text"
              min="0"
              max="100"
              step="0.1"
              placeholder="10.50"
              value={discountData.discountPercent || ''}
              onValueChange={(values) => {
                onConfigurationChange('data.discountPercent', values.floatValue || 0);
              }}
              className="w-full text-muted"
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted">
              %
            </span>
          </div>
          {errors.discountPercent && (
            <p className="text-sm text-destructive mt-1 dark:text-destructive">
              {errors.discountPercent}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1.5">
            Percentage to reduce the order total (0-100%).
          </p>
        </div>
      ) : (
        <div>
          <Label htmlFor="discountAmountInCents" className="text-sm font-medium">
            Discount Amount
          </Label>
          <div className="relative mt-1.5">
            <NumericFormat
              customInput={Input}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              id="discountAmountInCents"
              type="text"
              min="0"
              step="0.01"
              placeholder="$50.50"
              value={
                discountData.discountAmountInCents !== undefined &&
                discountData.discountAmountInCents !== null
                  ? safeMoneyFromCents(discountData.discountAmountInCents).toFormat()
                  : ''
              }
              onValueChange={(values) =>
                onConfigurationChange(
                  'data.discountAmountInCents',
                  Math.round((values.floatValue || 0) * 100),
                )
              }
              className="w-full text-muted"
            />
          </div>
          {errors.discountAmountInCents && (
            <p className="text-sm text-destructive mt-1 dark:text-destructive">
              {errors.discountAmountInCents}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1.5">
            Fixed dollar amount to subtract from the order total.
          </p>
        </div>
      )}
    </div>
  );
}
